// sapi-admin/src/pages/Settings/ShippingSettings.jsx
// Version corrigée

import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { 
  Truck, Save, RefreshCw, 
  MapPin, Package, Clock, Edit2, Check, 
  Globe, TrendingUp
} from 'lucide-react';
import { db } from '../../firebase/config';
import { getRegions } from '../../services/cameroonShipping';

// ============================================
// COMPOSANTS STYLISÉS
// ============================================

const GlassPanel = ({ children, style = {} }) => (
  <div style={{ 
    background: "rgba(17,24,39,.85)", 
    border: "1px solid rgba(255,255,255,.06)", 
    borderRadius: 16, 
    backdropFilter: "blur(16px)", 
    ...style 
  }}>
    {children}
  </div>
);

const Btn = ({ children, onClick, variant = 'ghost', size = 'sm', disabled = false, style = {} }) => {
  const V = { 
    ghost: { background: 'rgba(255,255,255,.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,.08)' },
    green: { background: 'rgba(16,185,129,.15)', color: '#34d399', border: '1px solid rgba(16,185,129,.3)' },
    red: { background: 'rgba(239,68,68,.15)', color: '#f87171', border: '1px solid rgba(239,68,68,.3)' },
    amber: { background: 'rgba(245,158,11,.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,.3)' },
    blue: { background: 'rgba(59,130,246,.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)' },
    purple: { background: 'rgba(139,92,246,.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,.3)' },
  };
  const v = V[variant] || V.ghost;
  const pad = size === 'xs' ? '3px 8px' : size === 'sm' ? '5px 12px' : '8px 18px';
  return <button onClick={onClick} disabled={disabled} style={{ ...v, padding: pad, borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .45 : 1, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'opacity .15s', whiteSpace: 'nowrap', ...style }}>{children}</button>;
};

const FormatPrice = (price) => {
  if (price === undefined || price === null) return '0 π';
  const num = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(num)) return '0 π';
  const fixed = num.toFixed(4);
  const trimmed = fixed.replace(/\.?0+$/, '');
  return `${trimmed} π`;
};

// ============================================
// COMPOSANTS ShippingField
// ============================================

const ShippingField = ({ label, value, onChange, editMode, toggleEdit, icon: Icon, color = '#94a3b8' }) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between',
    padding: '10px 12px',
    background: 'rgba(255,255,255,.03)',
    borderRadius: 10,
    border: editMode ? `1px solid ${color}44` : '1px solid rgba(255,255,255,.05)',
    transition: 'all .2s'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {Icon && <Icon size={14} color={color} />}
      <span style={{ fontSize: 11, color: '#94a3b8' }}>{label}</span>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {editMode ? (
        <input
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: 70,
            padding: '4px 8px',
            borderRadius: 6,
            border: `1px solid ${color}44`,
            background: 'rgba(0,0,0,.3)',
            color: '#e2e8f0',
            fontSize: 12,
            outline: 'none'
          }}
        />
      ) : (
        <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
          {value} π
        </span>
      )}
      <button
        onClick={toggleEdit}
        style={{
          padding: '4px 6px',
          borderRadius: 6,
          border: 'none',
          background: editMode ? 'rgba(16,185,129,.15)' : 'rgba(255,255,255,.06)',
          color: editMode ? '#34d399' : '#475569',
          cursor: 'pointer',
          transition: 'all .2s'
        }}
      >
        {editMode ? <Check size={12} /> : <Edit2 size={12} />}
      </button>
    </div>
  </div>
);

const ShippingFieldMultiplier = ({ label, value, onChange, editMode, toggleEdit }) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between',
    padding: '8px 12px',
    background: 'rgba(255,255,255,.03)',
    borderRadius: 8,
    border: editMode ? '1px solid rgba(139,92,246,.3)' : '1px solid rgba(255,255,255,.05)',
    transition: 'all .2s'
  }}>
    <span style={{ fontSize: 11, color: '#94a3b8' }}>{label}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {editMode ? (
        <input
          type="number"
          step="0.05"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: 50,
            padding: '3px 6px',
            borderRadius: 4,
            border: '1px solid rgba(139,92,246,.3)',
            background: 'rgba(0,0,0,.3)',
            color: '#e2e8f0',
            fontSize: 11,
            textAlign: 'center',
            outline: 'none'
          }}
        />
      ) : (
        <span style={{ fontSize: 13, fontWeight: 600, color: '#a78bfa' }}>
          ×{Number(value).toFixed(2)}
        </span>
      )}
      <button
        onClick={toggleEdit}
        style={{
          padding: '3px 5px',
          borderRadius: 4,
          border: 'none',
          background: editMode ? 'rgba(16,185,129,.15)' : 'rgba(255,255,255,.06)',
          color: editMode ? '#34d399' : '#475569',
          cursor: 'pointer'
        }}
      >
        {editMode ? <Check size={11} /> : <Edit2 size={11} />}
      </button>
    </div>
  </div>
);

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const ShippingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  const DEFAULT_REGION_MULTIPLIERS = {
    littoral: 0.5,
    centre: 0.5,
    nord_ouest: 0.8,
    sud_ouest: 0.8,
    ouest: 0.6,
    adamawa: 1.0,
    extreme_nord: 1.5,
    nord: 1.2,
    est: 1.5,
    sud: 0.6
  };

  const [settings, setSettings] = useState({
    cameroon: {
      standard: 0.5,
      express: 1.0,
      sameDay: 2.0,
      freeShippingThreshold: 50,
      regionMultipliers: DEFAULT_REGION_MULTIPLIERS
    },
    international: {
      standard: 1.0,
      express: 2.0,
      sameDay: 3.0
    }
  });
  
  const [editMode, setEditMode] = useState({});
  const [showPreview, setShowPreview] = useState(true);

  // ─── Charger les paramètres ────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const docRef = doc(db, 'settings', 'shipping');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const cameroon = data.cameroon || {};
        setSettings({
          cameroon: {
            standard: cameroon.standardPi ?? 0.5,
            express: cameroon.expressPi ?? 1.0,
            sameDay: cameroon.sameDayPi ?? 2.0,
            freeShippingThreshold: cameroon.freeShippingThresholdPi ?? 50,
            regionMultipliers: {
              ...DEFAULT_REGION_MULTIPLIERS,
              ...(cameroon.regionMultipliers || {})
            }
          },
          international: {
            standard: data.international?.standardPi ?? data.international?.standard ?? 1.0,
            express: data.international?.expressPi ?? data.international?.express ?? 2.0,
            sameDay: data.international?.sameDayPi ?? data.international?.sameDay ?? 3.0
          }
        });
        setLastSaved(data.updatedAt || new Date().toISOString());
      }
    } catch (error) {
      console.error('❌ Erreur chargement:', error);
      toast.error('Failed to load shipping settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Charger au montage ─────────────────────────────────────────────────
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // ─── Sauvegarder ────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'settings', 'shipping');
      await updateDoc(docRef, {
        cameroon: {
          standardPi: settings.cameroon.standard,
          expressPi: settings.cameroon.express,
          sameDayPi: settings.cameroon.sameDay,
          freeShippingThresholdPi: settings.cameroon.freeShippingThreshold,
          regionMultipliers: settings.cameroon.regionMultipliers
        },
        international: {
          standardPi: settings.international.standard,
          expressPi: settings.international.express,
          sameDayPi: settings.international.sameDay
        },
        updatedAt: serverTimestamp()
      });
      toast.success('✅ Shipping settings saved successfully!');
      setEditMode({});
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // ─── Modifier un champ ──────────────────────────────────────────────────
  const handleChange = (section, field, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) return;
    
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: numValue
      }
    }));
  };

  const handleRegionMultiplierChange = (regionId, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) return;
    setSettings(prev => ({
      ...prev,
      cameroon: {
        ...prev.cameroon,
        regionMultipliers: {
          ...prev.cameroon.regionMultipliers,
          [regionId]: numValue
        }
      }
    }));
  };

  const toggleEdit = (field) => {
    setEditMode(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // ============================================
  // RENDU
  // ============================================

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(20,184,166,.3)', borderTopColor: '#14b8a6', animation: 'spin .8s linear infinite' }} />
        <p style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Loading Shipping Settings…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ paddingBottom: 48 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* HEADER */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.18em', color: 'rgba(59,130,246,.8)', marginBottom: 4 }}>
              <Truck size={14} style={{ display: 'inline', marginRight: 4 }} /> Shipping
            </p>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>
              Shipping Settings
            </h1>
            <p style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>
              Configure shipping fees in Pi for Cameroon and international deliveries
            </p>
            {lastSaved && (
              <p style={{ fontSize: 9, color: '#334155', marginTop: 2 }}>
                Last saved: {new Date(lastSaved).toLocaleString()}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn variant="ghost" onClick={loadSettings} disabled={loading}>
              <RefreshCw size={13} style={{ animation: loading ? 'spin .8s linear infinite' : 'none' }} />
              Refresh
            </Btn>
            <Btn variant="green" onClick={handleSave} disabled={saving}>
              <Save size={13} />
              {saving ? 'Saving...' : 'Save Changes'}
            </Btn>
          </div>
        </div>
      </div>
      
      {/* GRILLE PRINCIPALE */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(380px,1fr))', gap: 20 }}>
        
        {/* CAMEROUN */}
        <GlassPanel>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
              <MapPin size={16} color="#34d399" /> Cameroon
            </h3>
            <span style={{ fontSize: 9, color: '#34d399', background: 'rgba(16,185,129,.1)', padding: '2px 10px', borderRadius: 12 }}>
              Domestic
            </span>
          </div>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            
            <ShippingField
              label="Standard (5-7 days)"
              value={settings.cameroon.standard}
              onChange={(val) => handleChange('cameroon', 'standard', val)}
              editMode={editMode['cameroon-standard']}
              toggleEdit={() => toggleEdit('cameroon-standard')}
              icon={Package}
              color="#34d399"
            />

            <ShippingField
              label="Express (2-3 days)"
              value={settings.cameroon.express}
              onChange={(val) => handleChange('cameroon', 'express', val)}
              editMode={editMode['cameroon-express']}
              toggleEdit={() => toggleEdit('cameroon-express')}
              icon={Truck}
              color="#60a5fa"
            />

            <ShippingField
              label="Same Day (24h)"
              value={settings.cameroon.sameDay}
              onChange={(val) => handleChange('cameroon', 'sameDay', val)}
              editMode={editMode['cameroon-sameDay']}
              toggleEdit={() => toggleEdit('cameroon-sameDay')}
              icon={Clock}
              color="#f59e0b"
            />

            <div style={{ borderTop: '1px solid rgba(255,255,255,.06)', paddingTop: 12, marginTop: 4 }}>
              <ShippingField
                label="Free Shipping Threshold (π)"
                value={settings.cameroon.freeShippingThreshold}
                onChange={(val) => handleChange('cameroon', 'freeShippingThreshold', val)}
                editMode={editMode['cameroon-threshold']}
                toggleEdit={() => toggleEdit('cameroon-threshold')}
                icon={Check}
                color="#a78bfa"
              />
            </div>
          </div>
        </GlassPanel>

        {/* INTERNATIONAL */}
        <GlassPanel>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Globe size={16} color="#f59e0b" /> International
            </h3>
            <span style={{ fontSize: 9, color: '#f59e0b', background: 'rgba(245,158,11,.1)', padding: '2px 10px', borderRadius: 12 }}>
              Worldwide
            </span>
          </div>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            
            <ShippingField
              label="Standard (7-10 days)"
              value={settings.international.standard}
              onChange={(val) => handleChange('international', 'standard', val)}
              editMode={editMode['international-standard']}
              toggleEdit={() => toggleEdit('international-standard')}
              icon={Package}
              color="#f59e0b"
            />

            <ShippingField
              label="Express (3-5 days)"
              value={settings.international.express}
              onChange={(val) => handleChange('international', 'express', val)}
              editMode={editMode['international-express']}
              toggleEdit={() => toggleEdit('international-express')}
              icon={Truck}
              color="#f59e0b"
            />

            <ShippingField
              label="Same Day (24h)"
              value={settings.international.sameDay}
              onChange={(val) => handleChange('international', 'sameDay', val)}
              editMode={editMode['international-sameDay']}
              toggleEdit={() => toggleEdit('international-sameDay')}
              icon={Clock}
              color="#f59e0b"
            />
          </div>
        </GlassPanel>
      </div>

      {/* ─── RÉGION MULTIPLIERS ────────────────────────────────────────────── */}
      <GlassPanel style={{ marginTop: 20 }}>
        <div style={{ padding: 16, borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <MapPin size={16} color="#a78bfa" />
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Region Multipliers</h3>
          <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 'auto' }}>fee = base rate × multiplier</span>
        </div>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 8 }}>
          {getRegions().map((region) => (
            <ShippingFieldMultiplier
              key={region.id}
              label={region.name}
              value={settings.cameroon.regionMultipliers?.[region.id] ?? 1.0}
              onChange={(val) => handleRegionMultiplierChange(region.id, val)}
              editMode={editMode[`region-${region.id}`]}
              toggleEdit={() => toggleEdit(`region-${region.id}`)}
            />
          ))}
        </div>
      </GlassPanel>

      {/* APERÇU DES CARTES */}
      <GlassPanel style={{ marginTop: 20 }}>
        <div style={{ padding: 16, borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUp size={16} color="#f59e0b" /> Fee Preview
          </h3>
          <button
            onClick={() => setShowPreview(!showPreview)}
            style={{ background: 'rgba(255,255,255,.05)', border: 'none', color: '#94a3b8', padding: '4px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 10 }}
          >
            {showPreview ? 'Hide' : 'Show'}
          </button>
        </div>
        
        {showPreview && (
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
            <div style={{ background: 'rgba(16,185,129,.08)', border: '1px solid rgba(16,185,129,.2)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
              <p style={{ fontSize: 8, color: '#34d399', textTransform: 'uppercase', letterSpacing: '.1em' }}>Standard</p>
              <p style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{FormatPrice(settings.cameroon.standard)}</p>
              <p style={{ fontSize: 9, color: '#475569' }}>Cameroon • 5-7 days</p>
            </div>
            <div style={{ background: 'rgba(59,130,246,.08)', border: '1px solid rgba(59,130,246,.2)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
              <p style={{ fontSize: 8, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '.1em' }}>Express</p>
              <p style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{FormatPrice(settings.cameroon.express)}</p>
              <p style={{ fontSize: 9, color: '#475569' }}>Cameroon • 2-3 days</p>
            </div>
            <div style={{ background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.2)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
              <p style={{ fontSize: 8, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '.1em' }}>Same Day</p>
              <p style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{FormatPrice(settings.cameroon.sameDay)}</p>
              <p style={{ fontSize: 9, color: '#475569' }}>Cameroon • 24h</p>
            </div>
            <div style={{ background: 'rgba(139,92,246,.08)', border: '1px solid rgba(139,92,246,.2)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
              <p style={{ fontSize: 8, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '.1em' }}>Free Shipping</p>
              <p style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>≥ {settings.cameroon.freeShippingThreshold} π</p>
              <p style={{ fontSize: 9, color: '#475569' }}>Threshold</p>
            </div>
          </div>
        )}
      </GlassPanel>

      {/* STATS RAPIDES */}
      <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12 }}>
        <div style={{ background: 'rgba(16,185,129,.05)', border: '1px solid rgba(16,185,129,.1)', borderRadius: 12, padding: 12 }}>
          <p style={{ fontSize: 9, color: '#34d399', textTransform: 'uppercase', letterSpacing: '.1em' }}>Cameroon</p>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
            Standard {settings.cameroon.standard}π • Express {settings.cameroon.express}π • Same Day {settings.cameroon.sameDay}π
          </p>
        </div>
        <div style={{ background: 'rgba(245,158,11,.05)', border: '1px solid rgba(245,158,11,.1)', borderRadius: 12, padding: 12 }}>
          <p style={{ fontSize: 9, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '.1em' }}>International</p>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
            Standard {settings.international.standard}π • Express {settings.international.express}π • Same Day {settings.international.sameDay}π
          </p>
        </div>
        <div style={{ background: 'rgba(139,92,246,.05)', border: '1px solid rgba(139,92,246,.1)', borderRadius: 12, padding: 12 }}>
          <p style={{ fontSize: 9, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '.1em' }}>Free Shipping</p>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
            ≥ {settings.cameroon.freeShippingThreshold} π
          </p>
        </div>
      </div>

    </motion.div>
  );
};

export default ShippingSettings;